// src/auth/AuthContext.jsx
import React, { createContext, useContext, useEffect, useState } from "react";
import { api, setAuthToken } from "../lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [ready, setReady] = useState(false);

  async function refreshMe() {
    try {
      const { data } = await api.get("auth/me");
      if (data?.ok) setUser(data.user);
      else setUser(null);
    } catch (e) {
      setUser(null);
    } finally {
      setReady(true);
    }
  }

  useEffect(() => {
    refreshMe();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function login({ name, email, password, tokenOverride = "", userOverride = null, skipRequest = false }) {
    const payload = {
      name: name ? String(name).trim() : "",
      email: email ? String(email).trim().toLowerCase() : "",
      password: String(password || ""),
    };

    try {
      const data = skipRequest
        ? { ok: true, token: tokenOverride, user: userOverride }
        : (await api.post("auth/login", payload)).data;

      if (!data?.ok) throw new Error(data?.message || "Login failed");

      if (data.token) setAuthToken(data.token);
      if (data.user) setUser(data.user);
      await refreshMe();

      return data;
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data;

      console.error("[auth] login error:", {
        message: e?.message,
        code: e?.code,
        status,
        body,
      });

      // show a useful message to UI
      const msg =
        body?.message ||
        (status ? `Login failed (${status})` : "") ||
        e?.message ||
        "Login failed";
      throw new Error(msg);
    }
  }

  async function logout() {
    try {
      await api.post("auth/logout");
    } catch {
      // ignore
    } finally {
      setAuthToken("");
      setUser(null);
    }
  }

  return (
    <AuthContext.Provider value={{ user, ready, login, logout, refreshMe }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
